import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getOrganizationDisplaySettings,
  updateOrganizationDisplaySettings,
  type OrganizationDisplaySettings,
} from '../../../utils/api';
import { ORGANIZATION_DISPLAY_SETTINGS_QUERY_KEY } from '../../../hooks/useOrganizationDisplaySettings';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { Badge } from '../../ui/badge';
import { Timestamp } from '../../ui/timestamp';
import { Clock3 } from 'lucide-react';

interface OrganizationSettingsPanelProps {
  title?: string;
  mode?: 'display' | 'features';
}

const displaySettingItems = [
  {
    key: 'show_costs' as const,
    title: 'Show cost information',
    description: 'Display classification costs in signal views and lists.',
  },
  {
    key: 'show_langchain_details' as const,
    title: 'Show LangChain details',
    description: 'Display LangSmith trace IDs and related execution metadata.',
  },
];

const featureSettingItems = [
  {
    key: 'root_cause_classification_enabled' as const,
    title: 'Root cause classification',
    description: 'Classify root causes for new records and keep historical output visible.',
  },
];

const clearRootCauseBannerSeenState = () => {
  try {
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith('go-root-cause-banner-seen:')) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage cleanup issues.
  }
};

const OrganizationSettingsPanel: React.FC<OrganizationSettingsPanelProps> = ({
  title = "Organization Settings",
  mode = 'display',
}) => {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState<OrganizationDisplaySettings>({
    show_costs: true,
    show_langchain_details: true,
    root_cause_classification_enabled: true,
    root_cause_classification_changed_at: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const isDisplayMode = mode === 'display';
  const visibleItems = isDisplayMode ? displaySettingItems : featureSettingItems;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrganizationDisplaySettings();
      setSettings(response);
    } catch (err) {
      setError("Failed to load organization settings");
      console.error("Error fetching organization settings:", err);
    } finally {
      setLoading(false);
    }
  };

  const buildSettingsPayload = () => ({
    show_costs: settings.show_costs,
    show_langchain_details: settings.show_langchain_details,
    root_cause_classification_enabled: settings.root_cause_classification_enabled,
  });

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);
      
      const updatedSettings = await updateOrganizationDisplaySettings(buildSettingsPayload());
      if (updatedSettings.root_cause_classification_enabled) {
        clearRootCauseBannerSeenState();
      }
      setSettings(updatedSettings);
      queryClient.setQueryData(ORGANIZATION_DISPLAY_SETTINGS_QUERY_KEY, updatedSettings);
      await queryClient.invalidateQueries({ queryKey: ORGANIZATION_DISPLAY_SETTINGS_QUERY_KEY });
      
      setSaveSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError("Failed to save organization settings");
      console.error("Error saving organization settings:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (setting: keyof Pick<OrganizationDisplaySettings, 'show_costs' | 'show_langchain_details' | 'root_cause_classification_enabled'>) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  if (loading) {
    return (
      <div className="rounded-[24px] border border-border bg-background p-6 shadow-[var(--shadow-card)]">
        <h2 className="font-jakarta text-lg font-bold text-foreground">{title}</h2>
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 w-3/4 rounded bg-muted"></div>
            <div className="h-4 rounded bg-muted"></div>
            <div className="h-4 w-5/6 rounded bg-muted"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[24px] border border-border bg-background p-6 shadow-[var(--shadow-card)]">
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-jakarta text-lg font-bold text-foreground">{title}</h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {isDisplayMode
              ? 'Manage the default debugging and metadata visibility used across your workspace.'
              : 'Enable or disable organization-level intelligence capabilities for new work.'}
          </p>
        </div>
        {saveSuccess && (
          <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[12px] font-medium text-emerald-700">
            Saved
          </div>
        )}
      </div>
      
      {error && (
        <div className="mb-4 rounded-2xl border border-destructive/20 bg-destructive/5 p-3 text-[13px] text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-3">
        <section className="rounded-[22px] border border-border bg-[var(--go-shell)] p-4">
          {!isDisplayMode && (
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="max-w-2xl">
                <p className="text-[13px] text-muted-foreground">
                  These controls are intended for feature access decisions, including future billable capabilities such as case guidance.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={settings.root_cause_classification_enabled ? 'default' : 'secondary'} className="capitalize">
                  {settings.root_cause_classification_enabled ? 'Active for new records' : 'Historical only'}
                </Badge>
                {settings.root_cause_classification_changed_at && (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock3 className="h-3.5 w-3.5" />
                    {settings.root_cause_classification_enabled ? 'Enabled' : 'Disabled'} on{' '}
                    <Timestamp
                      value={settings.root_cause_classification_changed_at}
                      format="absolute"
                    />
                  </span>
                )}
              </div>
            </div>
          )}
          <div className="space-y-3">
            {visibleItems.map((item) => (
              <div
                key={item.key}
                className="flex items-start justify-between gap-4 rounded-[20px] border border-border bg-background px-4 py-4"
              >
                <div className="min-w-0">
                  <h4 className="text-[14px] font-semibold text-foreground">{item.title}</h4>
                  <p className="mt-1 text-[13px] text-muted-foreground">{item.description}</p>
                </div>
                <Switch
                  checked={settings[item.key]}
                  onCheckedChange={() => handleToggle(item.key)}
                  className={settings[item.key] ? 'bg-primary' : 'bg-muted'}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
      
      <div className="mt-6 flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default OrganizationSettingsPanel;
